/**
 * Engine threat-volume kit — spatial danger fields (genre-neutral shapes).
 * Domain supplies volumes + weights; this kit only samples geometry.
 */

export type ThreatCircle = {
  kind: "circle";
  x: number;
  y: number;
  radius: number;
  weight: number;
};

export type ThreatCone = {
  kind: "cone";
  x: number;
  y: number;
  range: number;
  aimAngle: number;
  coneDeg: number;
  weight: number;
};

export type ThreatAnnulus = {
  kind: "annulus";
  x: number;
  y: number;
  innerR: number;
  outerR: number;
  weight: number;
};

export type ThreatVolume = ThreatCircle | ThreatCone | ThreatAnnulus;

function hypot2(dx: number, dy: number): number {
  return Math.hypot(dx, dy);
}

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Sum of weights for all volumes containing (x, y). */
export function sampleThreatAt(
  x: number,
  y: number,
  volumes: readonly ThreatVolume[],
): number {
  let sum = 0;
  for (const v of volumes) {
    if (v.weight <= 0) continue;
    if (v.kind === "circle") {
      if (hypot2(x - v.x, y - v.y) <= v.radius) sum += v.weight;
    } else if (v.kind === "annulus") {
      const d = hypot2(x - v.x, y - v.y);
      if (d >= v.innerR && d <= v.outerR) sum += v.weight;
    } else {
      const d = hypot2(x - v.x, y - v.y);
      if (d > v.range) continue;
      const ang = Math.atan2(y - v.y, x - v.x);
      const half = ((v.coneDeg * Math.PI) / 180) / 2;
      if (Math.abs(angleDiff(ang, v.aimAngle)) <= half) sum += v.weight;
    }
  }
  return sum;
}

/**
 * Approximate path cost along the segment (x0,y0)→(x1,y1) by sampling
 * `steps` interior points (inclusive ends). Higher = more dangerous transit.
 */
export function segmentThreatCost(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  volumes: readonly ThreatVolume[],
  steps = 8,
): number {
  const n = Math.max(2, Math.floor(steps));
  let cost = 0;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const x = x0 + (x1 - x0) * t;
    const y = y0 + (y1 - y0) * t;
    cost += sampleThreatAt(x, y, volumes);
  }
  return cost;
}
